/**
 * The analytics contract — Phase 5.
 *
 * Three rules from the phase prompt are encoded here rather than restated on
 * every page, because they are the ones that go wrong silently:
 *
 *  1. **Outcomes come from `v_closure`, never from `opportunities.status`.**
 *     Period attribution uses `close_date` and ignores both flags; "how did
 *     this deal end up" is `is_latest and not is_superseded_by_reopen`.
 *  2. **Pipeline membership comes from `opportunities.status`** — open pipeline
 *     is `status = 'open'` and nothing else.
 *  3. **Every figure states its revenue basis.** `REVENUE_BASIS_LABELS` is the
 *     wording, and `basis` travels *in the payload* beside every value block —
 *     so a chart cannot render a customer-basis figure without the label being
 *     available to print, the same trick Phase 3 used for the production-start
 *     label.
 */

import { z } from 'zod';
import { dateString, uuid } from './fields.js';
import { OPPORTUNITY_FLAG_FILTER_KEYS, opportunityFlagFilterSchema } from './flags.js';
import { accountScopeSchema } from './account-scope.js';

// ---------------------------------------------------------------------------
// Revenue basis — rule 3
// ---------------------------------------------------------------------------

/**
 * `net` is what Menlo books; `customer` is what the end customer pays and is
 * what reconciles against the distributor file (§5.4).
 *
 * **Every line-grain figure is customer basis**, because `net_factor` lives on
 * the opportunity's rep firm and a raw `annual_units × asp` has not been
 * through it. That covers `/analytics/products` entirely and
 * `pipeline_snapshots.business_unit_mix`.
 */
export const REVENUE_BASES = ['net', 'customer'] as const;
export type RevenueBasis = (typeof REVENUE_BASES)[number];

export const REVENUE_BASIS_LABELS: Record<RevenueBasis, string> = {
  net: 'Menlo net basis',
  customer: 'customer basis — not converted through the distributor net factor',
};

/**
 * What a production-start figure is, carried in the payload so a chart cannot
 * be rendered without it.
 *
 * It replaced §6.15's run-rate label ("annual run-rate at production start —
 * not a quarterly revenue forecast") when decision 7 made the chart spread each
 * deal's yearly revenue from its ramp date over its program life
 * (`docs/bugs.md` session 2 part 2). The figure is now the revenue that falls
 * in the period, so the label says that instead of warning that it is not.
 */
export const RAMP_SPREAD_LABEL =
  'revenue falling in each period — yearly revenue spread from the ramp date over the program life';

/** Said beside the count of deals the production-start chart could not place. */
export const UNSCHEDULED_NOTE =
  'Deals with no ramp date fall in no period. They still count toward lifetime value.';

/**
 * Why the design-win count and the win-rate tile deliberately disagree.
 *
 * The phase prompt requires this said on the page: a design-win count that
 * exceeds what the win-rate tile implies otherwise reads as a bug.
 */
export const IMPORTED_CHANNEL_NOTE =
  'Channel-imported wins (reason code IMPORTED_CHANNEL) are counted as design wins but are ' +
  'excluded from win rate and from every win-reason figure — the distributor file carries no ' +
  'losses, so including its wins would inflate the numerator against a denominator that never ' +
  'sees the other side.';

/** Why elapsed time and conversion drop the imported population entirely (§5.4 [R4]). */
export const IMPORTED_ELAPSED_NOTE =
  'Excludes channel-imported opportunities. For an imported win the created date, the single ' +
  'stage-history row and the closure date are all the same day, so every elapsed-time figure ' +
  'reads zero — and an imported deal inserted straight at Production entered no earlier stage, ' +
  'so it sits in no conversion numerator.';

/** Why opportunity counts do not sum on the business-unit charts (§5.1 [R2]). */
export const BUSINESS_UNIT_NOTE =
  'Business-unit figures are line-level. 18% of opportunities carry lines from two or three ' +
  'business units, so opportunity counts do not sum to the funnel total.';

// ---------------------------------------------------------------------------
// Filters — one set, every page, all of it in the URL
// ---------------------------------------------------------------------------

const csvList = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9_,-]+$/, 'expected a comma-separated list of codes');

/**
 * The filter set every `/api/analytics/*` route accepts.
 *
 * **`from`/`to` scope a different date column per endpoint**, and each response
 * names the one it used in `dateField`. A range picker that silently scoped
 * nothing would be worse than no picker, and there is no single date every
 * chart on this page shares — open pipeline has no date at all.
 */
export const analyticsFilterSchema = z.object({
  from: dateString.optional(),
  to: dateString.optional(),
  segment: csvList.optional(),
  region: csvList.optional(),
  ownerId: uuid.optional(),
  repFirmId: uuid.optional(),
  /**
   * One account **and every live account under it** (session 6,
   * `account-scope.ts`). The account strategy header reads its open pipeline
   * and won backlog through the same query the dashboard does, scoped by this
   * with `accountScope=exact`, so the two can never disagree about what those
   * words mean.
   */
  accountId: uuid.optional(),
  accountScope: accountScopeSchema.optional(),
  businessUnit: csvList.optional(),
  /**
   * Status and stage (session 5), the opportunity list's keys and values: a
   * CSV include list of `OPPORTUNITY_STATUSES` / `STAGE_CODES`, by the deal's
   * CURRENT status and stage. `status=all` is "All statuses" as in the list
   * (`expandStatusFilter`). An Invalid deal stays out of every figure unless
   * `invalid` is named here (decision 12).
   */
  status: csvList.optional(),
  stage: csvList.optional(),
  /**
   * The dashboard's role-aware toggle. `mine` resolves server-side to the
   * caller's own `owner_user_id`; it is not a second way to pass an id.
   */
  scope: z.enum(['mine', 'company']).optional(),
  /**
   * Focus and Verified, `yes` or `no` (session 4): the opportunity list's keys
   * and values, from the one schema in `flags.ts`. They read the deal's CURRENT
   * flags, snapshots included — snapshots are not extended (decision 17).
   */
  ...opportunityFlagFilterSchema.shape,
});

export type AnalyticsFilter = z.infer<typeof analyticsFilterSchema>;

/** Every filter key, so the client can round-trip them through the URL. */
export const ANALYTICS_FILTER_KEYS = [
  'from',
  'to',
  'segment',
  'region',
  'ownerId',
  'repFirmId',
  'accountId',
  'accountScope',
  'businessUnit',
  'status',
  'stage',
  'scope',
  ...OPPORTUNITY_FLAG_FILTER_KEYS,
] as const;

export type AnalyticsFilterKey = (typeof ANALYTICS_FILTER_KEYS)[number];

// ---------------------------------------------------------------------------
// Deal-size bands
// ---------------------------------------------------------------------------

/**
 * Annual revenue bands, on the **net** basis.
 *
 * Declared here rather than in SQL so the win-loss page, the velocity page and
 * the CSV export cannot disagree about where a band starts.
 */
export const DEAL_SIZE_BANDS = [
  { key: 'under_100k', label: 'Under $100k', min: 0, max: 100_000 },
  { key: '100k_500k', label: '$100k–$500k', min: 100_000, max: 500_000 },
  { key: '500k_1m', label: '$500k–$1M', min: 500_000, max: 1_000_000 },
  { key: '1m_plus', label: '$1M and above', min: 1_000_000, max: null },
] as const;

export type DealSizeBandKey = (typeof DEAL_SIZE_BANDS)[number]['key'];

// ---------------------------------------------------------------------------
// CSV export — one audited endpoint, an allowlist of datasets
// ---------------------------------------------------------------------------

/**
 * Every table on an analytics page exports through **one** endpoint.
 *
 * The dataset name is an allowlist key, never a query fragment: `POST
 * /api/analytics/export` resolves it to a SQL builder in
 * `services/analytics.ts`, so no request text reaches a statement. One
 * endpoint also means one place that writes the `audit_log` row (§6.9) and one
 * place that applies the 5,000-row cap.
 */
export const ANALYTICS_DATASETS = [
  'funnel',
  'concentration_accounts',
  'concentration_programs',
  'production_start',
  'stalls',
  'trend',
  'win_rate_by_dimension',
  'loss_reasons',
  'win_reasons',
  'competitors',
  'reclosed',
  'closures',
  'stage_dwell',
  'cycle_by_segment',
  'cycle_by_size_band',
  'cohort',
  'aging',
  'design_wins_by_quarter',
  'design_wins_by_program',
  'design_win_lapsed',
  'won_but_stalled',
  'products_by_part',
  'products_by_family',
  'products_by_business_unit',
  'products_alternates',
  'channel_by_firm',
  'channel_by_contact',
  'funnel_report',
] as const;

export type AnalyticsDataset = (typeof ANALYTICS_DATASETS)[number];

/**
 * The Funnel report — `docs/bugs.md` session 5, decision 17. One row per deal,
 * and **these columns and no others**, in this order.
 *
 * **Commercial information only** (§8, and the reason decision 17 lets every
 * tier export it): who, what, which stage, when it ramps and what it is worth.
 * No free-text field is in it — not `next_step`, `end_product`,
 * `application`, `on_hold_reason` or any note — because free text is the one
 * place technical data can be typed into this system by mistake (§8 item 4),
 * and a report has no business carrying it out again. A column is added here
 * deliberately; the test that pins this list says why.
 *
 * Money is on the **net** basis. The period columns are the slice of the
 * yearly figure falling inside `[period_start, period_end]`, from
 * `revenue_in_period()` — today's data, not the funnel as it stood then.
 */
export const FUNNEL_REPORT_COLUMNS = [
  'account',
  'opp_number',
  'opportunity',
  'owner',
  'stage',
  'status',
  'business_units',
  'segment',
  'focus',
  'verified',
  'ramp_date',
  'program_life_years',
  'yearly_revenue_net',
  'period_start',
  'period_end',
  'revenue_in_period_net',
  'weighted_revenue_in_period_net',
  'lifetime_value_net',
] as const;

/**
 * The statuses the production-start chart counts, and the funnel report's
 * default when no status is chosen (owner's ruling, session 5): open, won and
 * on hold — the deals that can still earn revenue. One constant, so the CSV's
 * total for a period equals the chart's for the same period and filters.
 * Lost and the rest are in the report only when named; Invalid likewise.
 */
export const PRODUCTION_START_STATUSES = ['open', 'won', 'on_hold'] as const;

/** Said wherever the funnel report is offered. */
export const FUNNEL_REPORT_NOTE =
  'One row per deal with revenue in the period: yearly revenue spread from the ramp date over ' +
  'the program life, from today’s data, on the net basis. Open, won and on-hold deals unless ' +
  'the Status filter names others — the same deals as the production-start chart. Deals with ' +
  'no ramp date fall in no period and are not in the file; the count is reported.';

export const analyticsExportSchema = z.object({
  dataset: z.enum(ANALYTICS_DATASETS),
  filters: analyticsFilterSchema.optional(),
});

export type AnalyticsExport = z.infer<typeof analyticsExportSchema>;

// ---------------------------------------------------------------------------
// Response shapes the client reads by name
// ---------------------------------------------------------------------------

/**
 * Money crosses the wire as a **string** — `node-pg` returns `numeric` as one
 * and §9 forbids the `setTypeParser` override that would make it a float.
 * Counts are integers and are typed as such.
 */
export type MoneyString = string;

export interface AnalyticsKpi {
  key: string;
  label: string;
  value: MoneyString | number | null;
  /** `money`, `count`, `percent` or `days` — decides the formatter, not the colour. */
  format: 'money' | 'count' | 'percent' | 'days';
  basis?: RevenueBasis;
  /** Signed change against the named prior period, in the same unit as `value`. */
  delta: MoneyString | number | null;
  deltaLabel: string | null;
  /** 26 weekly points from `pipeline_snapshots`, or null where snapshots cannot supply it. */
  sparkline: { date: string; value: MoneyString | number }[] | null;
  /** The denominator, where the value is a rate. Shown on the tile (never hidden). */
  denominator: number | null;
  /** One sentence naming the consequence, not the policy. */
  note: string | null;
}

export interface FunnelStageRow {
  stage_code: string;
  stage_name: string;
  sort_order: number;
  open_count: number;
  open_value_net: MoneyString;
  weighted_net: MoneyString;
  /** From `v_stage_progress`, **excluding imported opportunities** (§5.4 [R3]). */
  ever_entered: number;
  reached_later: number;
  conversion_pct: number | null;
}

export interface NetFactorAnnotation {
  date: string;
  rep_firm_id: string;
  rep_firm_name: string;
  old_value: string | null;
  new_value: string | null;
}
